/**
 * RestoreResultModal — Popup élégant après une tentative de restauration d'achats
 *
 * Remplace les Alert.alert natifs (moches, hors-style) par un modal cohérent
 * avec le design de l'app (overlay noir 55%, card blanche radius 24, icône
 * cerclée dorée, titre weight 300, CTA noir).
 *
 * 4 résultats possibles selon `kind` :
 *   - 'success'      → "Abonnement restauré" (vert)
 *   - 'already'      → "Abonnement déjà actif" (vert)
 *   - 'expired'      → "Abonnement expiré" (orange)
 *   - 'none'         → "Aucun abonnement à restaurer" (neutre)
 *   - 'error'        → "Erreur technique" (rouge)
 *
 * Usage :
 *   <RestoreResultModal
 *     visible={!!restoreResult}
 *     kind={restoreResult?.kind}
 *     details={restoreResult?.details}
 *     onClose={() => setRestoreResult(null)}
 *     onContactSupport={() => navigation.navigate('Support')}
 *   />
 */
import React from 'react';
import {
  View, Text, TouchableOpacity, StyleSheet,
} from 'react-native';
import AppModal from './AppModal';
import { useI18n } from '../i18n';

// v1.12.992 — Couleurs/icônes par état (textes localisés dans le composant)
const VARIANTS = {
  success: { icon: '✓', iconBg: '#ECFDF5', iconBorder: '#86EFAC', iconColor: '#059669' },
  already: { icon: '✓', iconBg: '#ECFDF5', iconBorder: '#86EFAC', iconColor: '#059669' },
  expired: { icon: '⏱', iconBg: '#FEF3C7', iconBorder: '#FCD34D', iconColor: '#D97706' },
  none:    { icon: '○', iconBg: '#F5EFE6', iconBorder: '#E8D9C8', iconColor: '#9B7B5A' },
  error:   { icon: '!', iconBg: '#FEF2F2', iconBorder: '#FCA5A5', iconColor: '#DC2626' },
};

export default function RestoreResultModal({
  visible,
  kind = 'none',
  details = null,
  onClose,
  onContactSupport = null,
}) {
  const { lang } = useI18n();
  const en = lang === 'en';
  if (!visible) return null;
  const v = VARIANTS[kind] || VARIANTS.none;

  const title = ({
    success: en ? 'Subscription restored' : 'Abonnement restauré',
    already: en ? 'Subscription already active' : 'Abonnement déjà actif',
    expired: en ? 'Subscription expired' : 'Abonnement expiré',
    none:    en ? 'Nothing to restore' : 'Rien à restaurer',
    error:   en ? 'Something went wrong' : 'Une erreur est survenue',
  })[kind] || (en ? 'Nothing to restore' : 'Rien à restaurer');

  const primaryLabel = ({
    success: en ? 'Great' : 'Parfait',
    already: en ? 'OK' : "D'accord",
    expired: en ? 'Got it' : 'Compris',
    none:    en ? 'OK' : "D'accord",
    error:   en ? 'Got it' : 'Compris',
  })[kind] || (en ? 'OK' : "D'accord");

  // Texte descriptif selon le cas (bilingue)
  const description = (() => {
    switch (kind) {
      case 'success':
        return en
          ? 'Your subscription is active again on this device.'
          : 'Votre abonnement est de nouveau actif sur cet appareil.';
      case 'already':
        return details?.tier && details?.period
          ? (en
              ? `Your ${details.tier} subscription (${details.period === 'yearly' ? 'yearly' : 'monthly'}) is already active.\n\nIf the app doesn't show this status, sign out and back in.`
              : `Votre abonnement ${details.tier} (${details.period === 'yearly' ? 'annuel' : 'mensuel'}) est déjà actif.\n\nSi l'app n'affiche pas ce statut, déconnectez-vous puis reconnectez-vous.`)
          : (en ? 'Your subscription is already active on this device.' : 'Votre abonnement est déjà actif sur cet appareil.');
      case 'expired':
        return en
          ? "A subscription was found for this account but it's no longer active. It may have been cancelled or is awaiting renewal.\n\nYou can reactivate it in your App Store settings."
          : (details?.allPurchases?.length
              ? `Un abonnement a été trouvé pour ce compte mais il n'est plus actif. Il a peut-être été annulé ou est en attente de renouvellement.\n\nVous pouvez le réactiver dans les réglages de votre App Store.`
              : 'Un ancien abonnement a été trouvé mais il n\'est plus actif.');
      case 'none':
        return en
          ? 'No subscription is associated with this account. To unlock all features, choose a plan below.'
          : 'Aucun abonnement n\'est associé à ce compte. Si vous souhaitez débloquer toutes les fonctionnalités, choisissez un plan ci-dessous.';
      case 'error':
        return details?.message
          ? (en
              ? `We couldn't verify your purchases right now:\n\n${details.message}\n\nTry restarting the app. If the problem persists, contact support@mytenue.com.`
              : `Impossible de vérifier vos achats pour le moment :\n\n${details.message}\n\nEssayez de redémarrer l'app. Si le problème persiste, contactez-nous à support@mytenue.com.`)
          : (en ? "We couldn't verify your purchases right now. Try restarting the app." : 'Impossible de vérifier vos achats pour le moment. Essayez de redémarrer l\'app.');
      default:
        return '';
    }
  })();

  return (
    <AppModal visible transparent animationType="fade" onRequestClose={onClose}>
      <View style={styles.overlay}>
        <View style={styles.card}>
          {/* Bouton de fermeture × en haut à droite */}
          <TouchableOpacity
            style={styles.closeX}
            onPress={onClose}
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
            activeOpacity={0.7}
          >
            <Text style={styles.closeXText}>×</Text>
          </TouchableOpacity>

          {/* Icône cerclée colorée selon le statut */}
          <View
            style={[
              styles.iconWrap,
              { backgroundColor: v.iconBg, borderColor: v.iconBorder },
            ]}
          >
            <Text style={[styles.iconChar, { color: v.iconColor }]}>{v.icon}</Text>
          </View>

          {/* Titre */}
          <Text style={styles.title}>{title}</Text>

          {/* Description */}
          <Text style={styles.desc}>{description}</Text>

          {/* Boutons */}
          <View style={styles.actions}>
            <TouchableOpacity
              style={styles.primaryBtn}
              onPress={onClose}
              activeOpacity={0.85}
            >
              <Text style={styles.primaryBtnText}>{primaryLabel}</Text>
            </TouchableOpacity>

            {/* Contact support uniquement en cas d'erreur (optionnel) */}
            {kind === 'error' && onContactSupport && (
              <TouchableOpacity
                style={styles.secondaryBtn}
                onPress={() => {
                  onClose();
                  setTimeout(() => onContactSupport(), 200);
                }}
                activeOpacity={0.7}
              >
                <Text style={styles.secondaryBtnText}>{en ? 'Contact support' : 'Contacter le support'}</Text>
              </TouchableOpacity>
            )}
          </View>
        </View>
      </View>
    </AppModal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.55)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  card: {
    width: '100%',
    maxWidth: 380,
    backgroundColor: '#FFFFFF',
    borderRadius: 24,
    paddingTop: 28,
    paddingHorizontal: 24,
    paddingBottom: 22,
    position: 'relative',
  },
  closeX: {
    position: 'absolute',
    top: 14,
    right: 14,
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#F5F5F5',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2,
  },
  closeXText: {
    fontSize: 18,
    color: '#666',
    fontWeight: '300',
    lineHeight: 22,
  },
  iconWrap: {
    width: 72,
    height: 72,
    borderRadius: 36,
    borderWidth: 2,
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: 'center',
    marginBottom: 18,
  },
  iconChar: {
    fontSize: 32,
    fontWeight: '300',
    lineHeight: 38,
  },
  title: {
    fontSize: 22,
    fontWeight: '300',
    color: '#1A1A1A',
    letterSpacing: 0.3,
    textAlign: 'center',
    marginBottom: 10,
  },
  desc: {
    fontSize: 13,
    color: '#666',
    textAlign: 'center',
    lineHeight: 20,
    marginBottom: 22,
    paddingHorizontal: 4,
  },
  actions: {
    gap: 10,
  },
  primaryBtn: {
    backgroundColor: '#1A1A1A',
    paddingVertical: 14,
    borderRadius: 100,
    alignItems: 'center',
  },
  primaryBtnText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#FFFFFF',
    letterSpacing: 0.5,
  },
  secondaryBtn: {
    paddingVertical: 12,
    alignItems: 'center',
  },
  secondaryBtnText: {
    fontSize: 13,
    fontWeight: '500',
    color: '#9B7B5A',
    letterSpacing: 0.3,
  },
});
